<template>
  <div
    class="product-options"
    :class="{ [$class('loading')]: loading }"
  >
    <div
      v-if="hasProductExtensions && product.extensions[0].extension?.title !== 'Fit'"
      class="product-options__option non-size-color inseam"
    >
      <div class="product-options__extension-container">
        <div
          v-if="product.extensions[0].extension?.title"
          class="product-options__extension-title-wrapper"
        >
          <legend
            class="product-options__extension-title text-body-2xs-regular"
            v-text="`${product.extensions[0].extension?.title}:`"
          />

          <span
            class="product-options__extension-value text-body-2xs-regular"
            v-text="currentExtensionName"
          />
        </div>

        <div class="product-options__extension">
          <selection-tab
            v-for="(extension) of product.extensions"
            :id="`${extension.extension.title}-${extension.extension.name}`"
            :key="extension.product.handle"
            v-model="currentExtensionName"
            :disabled="!extension.product.available"
            :label="getOptionLabel(extension.extension.name)"
            :name="extension.extension.title"
            :value="extension.extension.name"
            :title="getOptionLabel(extension.extension.name)"
          />
        </div>
      </div>
    </div>

    <div
      v-for="(option, optionIndex) of options"
      :key="getOptionKey(option)"
      class="product-options__option"
      :class="{ [option.name?.toLowerCase()]: true, [$class('hidden')]: !getDisplayOption(option) }"
    >
      <div
        v-if="(option.name === 'Size' || option.name === 'Taille') &&
          hasProductExtensions &&
          product.extensions[0].extension?.title === 'Fit'"
        class="product-options__extension-container"
      >
        <div
          v-if="product?.extensions[0].extension?.title"
          class="product-options__extension-title-wrapper"
        >
          <legend
            class="product-options__extension-title text-body-2xs-regular"
            v-text="`${product?.extensions[0]?.extension?.title}:`"
          />

          <span
            class="product-options__extension-value text-body-2xs-regular"
            v-text="currentExtensionName"
          />
        </div>

        <div class="product-options__extension">
          <selection-tab
            v-for="(object) of product.extensions"
            :id="`${object.metaobject?.extension}-${object.extension?.name}`"
            :key="`${object.metaobject?.extension}-${object.extension?.name}`"
            v-model="currentExtensionName"
            :label="getOptionLabel(object.extension?.name)"
            :name="object.metaobject?.extension"
            :value="object.extension?.name"
            :visually-disabled="!object.product.available"
          />
        </div>
      </div>

      <div
        v-if="displayOptionName"
        class="product-options__option-title-wrapper"
      >
        <legend
          class="product-options__option-title text-body-2xs-regular"
          :class="option.name.toLowerCase()"
          :for="option.name"
          v-html="`${getOptionDisplayName(option.name, option.values)}:`"
        />

        <span
          v-if="currentOptions[optionIndex]"
          class="product-options__option-value text-body-2xs-regular"
          v-html="getCurrentOptionName(currentOptions[optionIndex], option.name)"
        />

        <template v-if="option.name === 'Size' || option.name === 'Taille'">
          <span
            v-if="scarcityMessage"
            class="product-options__scarcity-message text-body-2xs-regular font-family-freight"
            v-text="scarcityMessage"
          />
        </template>
      </div>

      <template v-if="isSwatchOption(option.name)">
        <div class="product-options__swatches">
          <product-swatches
            :active-merge="activeMerge"
            block-class="product-options"
            :label="getOptionLabel(currentOptions[optionIndex])"
            :options="options"
            :option-name="option.name"
            product-form
            :product="product"
            :source="source"
            :is-dickey-bundle="isDickeyBundle"
            @update-merge="handleUpdateMerge"
            @update-product-handle="handleUpdateProductHandle"
            @swatch-mouse-enter="handleSwatchEnter"
            @swatch-mouse-leave="handleSwatchLeave"
          />
        </div>
      </template>

      <template
        v-else-if="isGiftCard || option.name === 'Taille' || option.name === 'Size' ||
          option.name === 'Denominations'"
      >
        <btn
          v-if="!isOneSize"
          class="product-options__size--mobile"
          :label="getMobileSizeText(optionIndex, option.name, option.values)"
          class-label="text-body-2xs-regular"
          modifiers="outline block"
          type="button"
          @click="openSizeOptionsMobile"
        />

        <teleport
          v-if="!isDickeyBundle"
          to="body"
        >
          <overlay
            key="sizeOptionsMobile"
            :block-class="showBackInStockModal ?
              'product-options-bis-mobile' :
              'product-options-size-mobile'
            "
            direction="up"
            mobile-drag-close
            namespace="sizeOptionsMobile"
            :show-close="false"
            type="shelf-modal"
          >
            <template
              v-if="!showBackInStockModal"
              #title
            >
              <h2
                class="product-options-size-mobile__title text-body-2xs-regular"
                :class="{ 'font-family-freight' : isProductPage }"
                v-text="getSelectOptionText(option.name, option.values)"
              />
            </template>

            <template #body>
              <klaviyo-bis-drawer
                v-if="showBackInStockModal"
                :cta-text="$string('product.back_in_stock.ok_cta')"
                :display-text="$string('product.back_in_stock.notify_email')"
                :product="product"
                :size-value="currentOptions[optionIndex]"
                :variant="variant"
                namespace="product-form"
                @close-drawer="showBackInStockModal = false"
                @form-submitted="handleKlaviyoFormSubmit"
              />

              <div
                v-if="!showBackInStockModal"
                class="product-options__values"
                :class="[
                  {
                    'product-options__values--one-size' : isOneSize ,
                    [$class('disabled')]: isShippingUnavailable && blockedProductsEnabled,
                  }
                ]"
              >
                <selection-tab
                  v-for="(value, valueIndex) of option.values"
                  :id="getOptionValueKey(value, valueIndex, option)"
                  :key="getOptionValueKey(value, valueIndex, option)"
                  v-model="currentOptions[option.index]"
                  class="product-options__value"
                  :disabled="getValueDisabled(option.disabled[value], 'disabled')"
                  :label="getOptionLabel(option.labels[valueIndex])"
                  :low-stock="getValueLowStock(value, optionIndex)"
                  :name="getOptionKey(option)"
                  :preorder="!preorderVariants && option.preorder[value]"
                  :value="value"
                  :visually-disabled="getValueDisabled(option.disabled[value], 'visually-disabled')"
                />

                <div
                  v-if="!isOneSize"
                  class="product-options-size-mobile__size-guide-wrapper"
                >
                  <div
                    v-if="isShippingUnavailable && blockedProductsEnabled"
                    class="product-form__shipping-unavailable"
                    v-text="$string('product.form.shipping_unavailable')"
                  />

                  <btn
                    v-if="!isGiftCard"
                    :label="$string('product.form.size_guide')"
                    class="product-options-size-mobile__size-guide"
                    class-label="text-body-2xs-regular"
                    modifiers="text center"
                    type="button"
                    @click="openSizeGuide"
                  />
                </div>
              </div>
            </template>
          </overlay>
        </teleport>

        <div
          v-if="!isOneSize"
          class="product-options__size--desktop"
          :class="{ [$class('disabled')]: isShippingUnavailable && blockedProductsEnabled }"
        >
          <div
            class="product-options__values"
            :class="{ 'product-options__values--dickey-sizing': isDickey }"
          >
            <selection-tab
              v-for="(value, index) of option.values"
              :id="getOptionValueKey(value, index, option)"
              :key="getOptionValueKey(value, index, option)"
              v-model="currentOptions[option.index]"
              class="product-options__value"
              :class="`dickey-bundle-${value}`"
              :disabled="getValueDisabled(option.disabled[value], 'disabled')"
              :label="getOptionLabel(option.labels[index])"
              :name="getOptionKey(option)"
              :value="value"
              :visually-disabled="getValueDisabled(option.disabled[value], 'visually-disabled')"
            />
          </div>
        </div>
      </template>

      <template v-else>
        <div
          v-if="section.dropdownOptions?.includes(getOptionHandle(option.name))"
          class="form-group"
        >
          <label
            class="visually-hidden"
            :for="getOptionKey(option)"
            v-text="option.name"
          />

          <select
            :id="getOptionKey(option)"
            v-model="currentOptions[option.index]"
            class="product-options__select"
          >
            <option
              v-if="section.excludedInitialSelectOptions?.includes(getOptionHandle(option.name))"
              value=""
              v-text="getSelectOptionText(option.name, option.values)"
            />

            <option
              v-for="(value, index) of option.values"
              :id="getOptionValueKey(value, index, option)"
              :key="getOptionValueKey(value, index, option)"
              :disabled="getValueDisabled(option.disabled[value], 'disabled')"
              :value="value"
              v-text="getDropdownOptionText(option, value, index)"
            />
          </select>
        </div>

        <div
          v-else
          class="product-options__values"
        >
          <selection-tab
            v-for="(value, index) of option.values"
            :id="getOptionValueKey(value, index, option)"
            :key="getOptionValueKey(value, index, option)"
            v-model="currentOptions[option.index]"
            class="product-options__value"
            :disabled="getValueDisabled(option.disabled[value], 'disabled')"
            :label="option.labels[index]"
            :name="getOptionKey(option)"
            :value="value"
            :visually-disabled="getValueDisabled(option.disabled[value], 'visually-disabled')"
          />
        </div>
      </template>
    </div>

    <select
      v-model="currentVariantId"
      class="product-options__variant-select visually-hidden"
      name="id"
      aria-hidden="true"
      disabled
    >
      <option
        v-for="(variantObject, index) of product.variants"
        :key="getVariantKey(variantObject, index)"
        :value="variantObject.id"
        v-text="`${variantObject.title} - ${variantObject.id}`"
      />
    </select>
  </div>
</template>

<script>

/**
 * Vue: Product options (product-form)
 * -----------------------------------------------------------------------------
 * Product variants split into separate options and values.
 *
 * @param {Object} activeMerge - Active group object.
 * @param {String} isGiftCard - Product is a gift card product.
 * @param {Boolean} [loading] - Loading state.
 * @param {Boolean|Object} product - Canvas formatted product object.
 * @param {Object} section - Main product section settings.
 * @param {Boolean|Null|Object} variant - Canvas formatted variant object.
 * @param {Boolean} isShippingUnavailable - If shipping is unavailable.
 *
 * @emits formSubmitted - Klaviyo back in stock form submitted.
 * @emits updateDickey - Update Dickey boolean state.
 * @emits updateMerge - Update merge object.
 * @emits updateProductHandle - Update current product handle.
 * @emits updateVariant - Emit new variant object.
 *
 */
import { mapActions, mapGetters } from 'vuex'

import Btn from '~global/btn/btn'
import KlaviyoBisDrawer from '~async/klaviyo-bis/klaviyo-bis-drawer'
import Overlay from '~global/overlay/overlay'
import ProductSwatches from '~global/product-swatches/product-swatches'
import SelectionTab from '~async/utils/selection-tab/selection-tab'

import { convertToHandle } from '~/helpers/convert'
import { emitUpdateVariant } from '~/helpers/vue'

export default {
  name: 'ProductOptions',

  components: {
    Btn,
    KlaviyoBisDrawer,
    Overlay,
    ProductSwatches,
    SelectionTab,
  },

  props: {
    activeMerge: {
      type: Object,
      default: () => ({}),
    },
    isGiftCard: {
      type: Boolean,
      default: false,
    },
    loading: {
      type: Boolean,
      default: false,
    },
    product: {
      type: [Boolean, Object],
      default: false,
      required: true,
    },
    section: {
      type: Object,
      default: () => ({
        displaySingleOptionNames: true,
        displaySingleValueOptions: false,
        displaySingleVariantOption: false,
        dropdownOptions: [],
        excludedInitialSelectOptions: ['size', 'taille', 'denominations'],
        hideGiftCardOptionNames: true,
        id: '',
        selectDisabledValues: true,
      }),
      required: true,
    },
    variant: {
      type: [Boolean, null, Object],
      default: false,
      required: true,
    },
    isDickeyBundle: {
      type: Boolean,
      default: false,
    },
    isShippingUnavailable: {
      type: Boolean,
      default: false,
    },
    source: {
      type: String,
      default: '',
    },
  },

  emits: {
    'form-submitted': () => true,
    'update-dickey': (isDickey) => typeof isDickey === 'boolean',
    'update-merge': (payload) => typeof payload === 'object',
    'update-product-handle': (payload) => typeof payload === 'string',
    'update-variant': emitUpdateVariant,
  },

  data() {
    return {
      currentExtensionName: '',
      currentOptions: [],
      currentVariantId: 0,
      options: [],
      showBackInStockModal: false,
      hoveredOptions: null,
    }
  },

  computed: {

    /**
     * Map Vuex getters.
     */
    ...mapGetters({
      getActiveOverlay: 'overlays/getActiveOverlay',
    }),

    /**
     * Compute whether blocked products are enabled.
     * @returns {Boolean}
     */
    blockedProductsEnabled() {
      return window.cnvs?.settings?.blockedProduct || false
    },

    /**
     * Compute if option name should be displayed.
     * @returns {Boolean}
     */
    displayOptionName() {
      if (
        this.section.displaySingleOptionNames ||
        (this.section.displaySingleValueOptions && this.options.length > 1)
      ) {
        return true
      }

      const multiValueOptions = this.options.filter((option) => {
        return option.values.length > 1
      })

      return multiValueOptions.length > 1
    },

    /**
     * Compute if this product has product extensions.
     * @returns {Boolean}
     */
    hasProductExtensions() {
      return this.product?.extensions?.length > 1
    },

    /**
     * Compute if product is a dickey jacket.
     * @returns {Boolean}
     */
    isDickey() {
      const sizeOption = this.options?.find(option => option.name === 'Size' || option.name === 'Taille')
      return sizeOption?.values?.length === 2
    },

    /**
     * Compute if product has one size.
     * @returns {Boolean}
     */
    isOneSize() {
      return this.product?.variants?.length === 1
    },

    /**
     * Compute scarcity message.
     * @returns {String|Boolean}
     */
    scarcityMessage() {
      if (!this.variant || !this.variant?.available || this.product.giftCard) {
        return false
      }

      if (this.variant?.inventory <= this.$variable('settings.lowStockThreshold')) {
        return `${this.$string('product.form.low_stock')}`
      }

      return false
    },

    /**
     * Compute if all variants are preorders.
     * @returns {Boolean}
     */
    preorderVariants() {
      return this.product?.variants?.every((variant) => {
        return this.product?.preorder?.skus?.includes(variant.sku)
      })
    },

    /**
     * Computes if the page is PDP
     * @returns {Boolean}
     */
    isProductPage() {
      return window.cnvs?.page.type === 'product'
    },
  },

  watch: {

    /**
     * Watch current extension name to find and update product.
     * @param {Object} name - Selected extension name.
     */
    currentExtensionName(name) {
      const newExtension = this.product.extensions.find((object) => {
        return object.extension?.name === name
      })

      if (!newExtension) {
        return
      }

      this.$emit('update-merge', newExtension)
      this.$emit('update-product-handle', newExtension.product.handle)
    },

    /**
     * Watch current options to update based on option choices.
     */
    currentOptions: {
      handler() {
        this.getOptions()
        const variantTitle = this.currentOptions.join(' / ')
        const variant = this.getVariantFromProperty(variantTitle, 'title')
        this.currentVariantId = variant ? variant.id : 0
      },
      deep: true,
    },

    /**
     * Watch currentVariantId to update Canvas variant when select changes.
     */
    currentVariantId() {
      if (this.variant?.id === this.currentVariantId) {
        return
      }

      const variant = this.getVariantFromProperty(this.currentVariantId)
      this.$emit('update-variant', variant)
      this.$emit('update-dickey', this.isDickey)
      if (variant && variant.available === false) {
        this.showBackInStockModal = true
      } else {
        this.closeOverlay('sizeOptionsMobile')
      }
    },

    /**
     * Watch product to update selected variant when product data is loaded.
     * @param {Boolean|Object} newValue - New product value.
     * @param {Boolean|Object} oldValue - Old product value.
     */
    product(newValue, oldValue) {
      if (newValue?.id === oldValue?.id) {
        return
      }

      this.currentOptions = []
      this.getCurrentVariant()
      this.setCurrentExtensionName()
    },

    /**
     * Watch variant for updates to update current options.
     * - If variant is set to false (reset form) then run mounted methods.
     * - Close size modal if needed
     */
    variant() {
      if (this.variant === false) {
        this.getOptions()
        this.getCurrentVariant()
        return
      }

      this.getCurrentOptions()

      if (this.getActiveOverlay?.namespace === 'sizeOptionsMobile' && !this.variant.available) {
        this.showBackInStockModal = true
        return
      }

      this.closeOverlay('sizeOptionsMobile')
    },
  },

  mounted() {
    this.getOptions()
    this.getCurrentVariant()
    this.setCurrentExtensionName()
  },

  methods: {

    /**
     * Map Vuex actions.
     */
    ...mapActions({
      closeOverlay: 'overlays/close',
      openOverlay: 'overlays/open',
      setSizeGuideProduct: 'sizeGuideDrawer/setSizeGuideProduct',
    }),

    /**
     * Checks if option contains numeric values
     */
    isValidNumericSize(options) {
      return options.every(size => !isNaN(size) && (/^[0-9]+(\.[0-9]+)?$/).test(size))
    },

    /**
     * Checks for non-US country and numeric sizes
     */
    isNonUSNumericSize(optionName, options) {
      return Shopify.country !== 'US' && Shopify.country !== 'CA' && (optionName.toLowerCase() === 'size' || optionName.toLowerCase() === 'taille') && this.isValidNumericSize(options)
    },

    /**
     * Get if single value options should be displayed.
     * @param {Object} option - Option object.
     * @returns {Boolean}
     */
    getDisplayOption(option) {
      if ((option.name === 'Size' && this.variant?.selectedOptions?.filter((opt) => opt.name === 'Size')[0].value === 'OS') ||
        (option.name === 'Taille' && this.variant?.selectedOptions?.filter((opt) => opt.name === 'Taille')[0].value === 'Taille Unique')
      ) {
        return false
      }

      if (this.$variable('settings.product.productSwatchOptionNames')?.includes(option.name?.toLowerCase())) {
        return true
      }

      if (this.section.displaySingleValueOptions) {
        return true
      }

      return option.values.length > 1
    },

    /**
     * Text for the button that toggles the mobile size options overlay.
     * @param {Number} index - Size option index
     * @param {String} name - Size option name
     * @returns {String}
     */
    getMobileSizeText(index, name, options) {
      const isSizeSet = this.currentOptions && this.currentOptions[index]

      if (!isSizeSet) {
        return this.getSelectOptionText(name, options)
      }

      let currentSizeLabel = this.currentOptions[index]

      if (this.scarcityMessage) {
        currentSizeLabel += ` ${this.scarcityMessage}`
      }

      if (Shopify.country !== 'US' && Shopify.country !== 'CA' && this.isValidNumericSize(options)) {
        currentSizeLabel = this.$string('product.form.selected_option_non_us', { replace: { option: currentSizeLabel } })
      }
      return currentSizeLabel
    },

    /** Get option display name to display 'size' as 'amount' for gift cards.
     * @param {String} optionName - Name as it appears in Shopify admin.
     * @returns {String}
     */
    getOptionDisplayName(optionName, options) {
      if ((this.product.giftCard || this.isGiftCard) || optionName.toLowerCase() === 'denominations') {
        return this.$string('gift_card.amount')
      }

      if (this.isNonUSNumericSize(optionName, options)) {
        return this.$string('product.form.selected_option_non_us', { replace: { option: optionName } })
      }

      return optionName
    },

    /**
     * Get option key.
     * @param {Object} option - Option object.
     * @returns {String}
     */
    getOptionKey(option) {
      return `${this.section.id}-${this.product.handle}-option-${option.index}-${this.getOptionHandle(option.name)}`
    },

    /**
     * Get option handle.
     * @param {String} name - Option name.
     * @returns {String}
     */
    getOptionHandle(optionName) {
      return convertToHandle(optionName)
    },

    /**
     * Get option value key/id.
     * @param {String} value - Value name.
     * @param {Number} index - Forloop index.
     * @param {Object} option - Option object.
     * @returns {String}
     */
    getOptionValueKey(value, index, option) {
      return `${this.getOptionKey(option)}-value-${index}-${this.getOptionHandle(value)}`
    },

    /**
     * Get dropdown text.
     * @param {Object} option - Option object.
     * @param {String} value - Current option value.
     * @param {Number} index - Current option value index.
     * @returns {String}
     */
    getDropdownOptionText(option, value, index) {
      const label = option.labels[index]

      if (this.getValueDisabled(option.disabled[value], 'visually-disabled')) {
        return `${label} - ${this.$string('product.form.sold_out')}`
      }

      return label
    },

    /**
     * Get variant key.
     * @param {Object} variant - Variant object.
     * @param {Number} index - Forloop index.
     * @returns {String}
     */
    getVariantKey(variant, index) {
      return `${this.product.handle}-option-${index}-${this.getOptionHandle(variant.title)}`
    },

    /**
     * Get if value is disabled.
     * - Based on section setting and attribute.
     * @param {Boolean} available - Value availability.
     * @param {String} attribute - Selection tab attribute.
     * @returns {Boolean}
     */
    getValueDisabled(available, attribute) {
      if (!available) {
        return false
      }

      return (
        (attribute === 'disabled' && !this.section.selectDisabledValues) ||
        (attribute === 'visually-disabled' && this.section.selectDisabledValues)
      )
    },

    /**
     * Get if value is low-stock.
     * @param {String} value - Option value to check.
     * @param {Number} optionIndex - Index of option this value belongs to.
     * @returns {Boolean}
     */
    getValueLowStock(value, optionIndex) {
      if (this.product.giftCard) {
        return false
      }

      const matchedVariant = this.product.variants?.find(variant => {
        return variant.selectedOptions[optionIndex].value === value
      })

      return matchedVariant?.available &&
        matchedVariant?.inventory <= this.$variable('settings.lowStockThreshold')
    },

    /**
     * Get extended options data.
     * - Extends product data with disabled and handle data.
     * - When `loading` is `true` and `product` is `false` then this method
     *   returns placeholder options to render in the loading state.
     * @returns {Array}
     */
    getOptions() {
      if (this.loading && !this.product) {
        const values = [...new Array(3)].map(() => {
          return this.$string('accessibility.loading')
        })

        this.options = [...new Array(3)].map((_, index) => {
          return {
            disabled: true,
            handle: `storybook-${index}`,
            index,
            labels: values,
            name: this.$string('accessibility.loading'),
            values,
          }
        })

        return
      }

      if (!this.product?.options?.length) {
        this.options = []
        return
      }

      this.options = this.product.options.map((option, optionIndex) => {
        const disabled = {}
        const preorder = {}

        option.values.forEach((value) => {

          /**
           * Find variants that match current value.
           * - Then find variants that match other currentOptions.
           */
          const matchingVariants = this.product.variants
            .filter((variant) => {
              return variant.selectedOptions[optionIndex].value === value
            })
            .filter((variant) => {
              return this.currentOptions.every((currentOption, currentOptionIndex) => {
                if (!currentOption || optionIndex === currentOptionIndex) {
                  return true
                }

                return (
                  variant.selectedOptions[currentOptionIndex]?.value?.toLowerCase() ===
                  currentOption?.toLowerCase()
                )
              })
            })

          if (!matchingVariants.length) {
            return
          }

          /**
           * Determine if option is unavailable.
           * - Test to see if all matching variants are unavailable.
           */
          disabled[value] = matchingVariants.every((variant) => {
            return variant && !variant.available
          })

          /**
           * Determine if any linked variants are preorder.
           * - Test if some SKUs are includes in preorder SKUs metafield.
           */
          preorder[value] = matchingVariants.some((variant) => {
            return this.product?.preorder?.skus?.includes(variant.sku)
          })

          preorder[value] = preorder[value] ? this.product?.preorder?.date : false
        })

        /**
         * Return extended data.
         */
        return {
          ...option,
          disabled,
          handle: this.getOptionHandle(option.name),
          preorder,
        }
      })
    },

    /**
     * Get current variant on page load or product update.
     */
    getCurrentVariant() {
      if (!this.product || !this.product?.variants?.length) {
        return
      }

      if (this.$variable('storybook') && this.variant) {
        this.getCurrentOptions()
        return
      }

      /**
       * Always get current variant from URL.
       */
      const variantFromUrl = this.getVariantFromUrl()

      if (variantFromUrl) {
        this.currentVariantId = variantFromUrl.id
        return
      }

      /**
       * Pick the first in stock variant.
       * - If no variants are in stock then use first variant.
       */
      const inStock = this.product.variants.filter((variant) => variant.available)
      const currentVariant = inStock.length ? inStock[0] : this.product.variants[0]

      /**
       * Only set current variant if none of the variant options are excluded
       * from being initially set.
       * - Except where excluded option has only a single value, otherwise if
       *   single value options are not shown the variant will not be
       *   selectable by the user.
       */
      const currentOptions = currentVariant.selectedOptions.map((selectedOption) => {
        const optionHandle = this.getOptionHandle(selectedOption.name)
        const matchedOption = this.options.find((option) => option.handle === optionHandle)

        const excludedOption =
          this.section.excludedInitialSelectOptions.includes(optionHandle) &&
          matchedOption.values.length > 1

        return excludedOption ? '' : selectedOption.value
      })

      const filteredCurrentOptions = currentOptions.filter(Boolean)

      if (filteredCurrentOptions.length !== currentVariant.selectedOptions.length) {
        this.currentOptions = currentOptions
        return
      }

      this.currentVariantId = currentVariant.id
    },

    /**
     * Get initial variant from URL parameter.
     * @returns {Object}
     */
    getVariantFromUrl() {
      const params = new URLSearchParams(document.location.search.substring(1))
      const variantId = Number(params.get('variant'))
      return this.getVariantFromProperty(variantId)
    },

    /**
     * Get variant object from variant property.
     * @param {String} value - Value to match.
     * @param {String} property - Property to find by.
     * @returns {Object|Null}
     */
    getVariantFromProperty(value, property = 'id') {
      const matchingVariant = this.product.variants?.find(variant => {
        if (typeof value === 'string') {
          return variant[property].toLowerCase() === value.toLowerCase()
        }

        return variant[property] === value
      })

      return matchingVariant ? matchingVariant : null
    },

    /**
     * Get current options.
     * - Boolean variant is used to reset current options.
     * - Null variant just means selected options hasn't found a variant
     *   and shouldn't reset the current options.
     */
    getCurrentOptions() {
      if (!this.variant) {
        if (this.variant === null) {
          return
        }

        this.currentOptions = Array.from(Array(this.product?.options?.length)).map(() => '')

        if (this.activeMerge?.swatch?.name) {
          this.currentOptions.splice(0, 1, this.activeMerge?.swatch?.name)
        }

        return
      }

      this.variant.selectedOptions.forEach((selectedOption, index) => {
        this.currentOptions[index] = selectedOption.value
      })
    },

    /**
     * Handle update product merge.
     * @param {Object} group - New group.
     */
    handleUpdateMerge(object) {
      this.currentOptions[0] = object?.swatch?.name

      this.$emit('update-merge', object)
      this.$emit('update-dickey', this.isDickey)
    },

    /**
     * If a given option displays as swatches
     * @param {String} name - Option name.
     */
    isSwatchOption(name) {
      return this.$variable('settings.product.productSwatchOptionNames')?.includes(name.toLowerCase())
    },

    /**
     * Get 'select option' user prompt text.
     * @param {String} optionName - Name as it appears in Shopify admin.
     */
    getSelectOptionText(optionName, options) {
      if ((this.product.giftCard || this.isGiftCard) || optionName.toLowerCase() === 'denominations') {
        return this.$string('gift_card.select_amount')
      }

      if (this.isNonUSNumericSize(optionName, options)) {
        return this.$string('product.form.select_option_non_us', { replace: { option: optionName.toLowerCase() } })
      }

      return this.$string('product.form.select_option', { replace: { option: optionName.toLowerCase() } })
    },

    /**
     * Get current option label.
     * @param {String} label - Label.
     * @returns {String}
     */
    getOptionLabel(label) {
      if (label === 'OS') {
        return this.$string('product.form.one_size')
      }

      if (Shopify.country !== 'US' && Shopify.country !== 'CA' && (/^[0-9]+(\.[0-9]+)?$/).test(label)) {
        return this.$string('product.form.selected_option_non_us', { replace: { option: label } })
      }

      return label
    },

    /**
     * Open size guide drawer.
     */
    openSizeGuide() {
      this.setSizeGuideProduct(this.product)

      this.openOverlay({
        ignoreDismissed: true,
        namespace: 'sizeGuideDrawer',
      })
    },

    /**
     * Open mobile size options drawer.
     */
    openSizeOptionsMobile() {

      /**
       * Display back in stock modal when opening size drawer if size is out of
       * stock
       */
      const sizeIndex = this.options?.findIndex(option => option.name === 'Size' || option.name === 'Taille')
      const currentSize = this.currentOptions[sizeIndex]
      this.showBackInStockModal = this.options[sizeIndex]?.disabled[currentSize]

      this.openOverlay({
        ignoreDismissed: true,
        namespace: 'sizeOptionsMobile',
      })
    },

    /**
     * Set current extension name.
     */
    setCurrentExtensionName() {
      if (!this.hasProductExtensions) {
        return
      }

      const matchedExtension = this.product?.extensions?.find((object) => {
        return object.product.handle === this.product.handle
      })

      this.currentExtensionName = matchedExtension?.extension?.name || ''
    },

    /**
     * Handle klaviyo form submit event.
     */
    handleKlaviyoFormSubmit() {
      this.$emit('form-submitted')
    },

    /**
     * Handle update product handle.
     * @param {Object} handle - Product handle to update.
     */
    handleUpdateProductHandle(handle) {
      this.$emit('update-product-handle', handle)
    },

    /**
     * Display Selected option
     */
    getCurrentOptionName(currentOption, optionText) {
      if (this.hoveredOptions &&
        (this.hoveredOptions?.swatchHandle.toLowerCase() === optionText.toLowerCase())) {
        return this.hoveredOptions.swatchName
      }

      if (Shopify.country !== 'US' && Shopify.country !== 'CA' && (/^[0-9]+(\.[0-9]+)?$/).test(currentOption)) {
        return this.$string('product.form.selected_option_non_us', { replace: { option: currentOption } })
      }

      return currentOption
    },

    /*
     * Handle swatch enter emit event
     * @param {Object} swatcObject hover swatch name and label
     */
    handleSwatchEnter(swatcObject) {
      this.hoveredOptions = swatcObject
    },

    /**
     * handl swatch leave emit event
     */
    handleSwatchLeave() {
      this.hoveredOptions = false
    },
  },
}
</script>

<style lang="scss">
@import './product-options';
</style>
